import { uniqBy } from 'lodash-es';
import { FieldType } from 'models/forms/fields/FieldMetadata';
import { type FieldFormatter, type FieldValueValidator, isHandledBySamePredicate } from 'models/forms/fields/utils';

export type AvailableItemsFieldBase<AvailableItem = string> = {
  availableItems: AvailableItems<AvailableItem>;
};
export function isAvailableItemsField<Item>(val: unknown): val is AvailableItemsFieldBase<Item> {
  return (val as AvailableItemsFieldBase<Item>).availableItems !== undefined;
}
export function mergeAvailableItems<T>(
  firstAvailableItems: AvailableItems<T>,
  secondAvailableItems: AvailableItems<T>,
): AvailableItems<T> {
  return uniqBy([...firstAvailableItems, ...secondAvailableItems], item => item.value);
}

export function mergeAvailableItemsFields<Item, T extends AvailableItemsFieldBase<Item>>(
  firstField: T,
  secondField: T,
): T {
  return { ...firstField, availableItems: mergeAvailableItems(firstField.availableItems, secondField.availableItems) };
}
export type AvailableItems<T = string> = ReadonlyArray<AvailableItem<T>>;

export interface AvailableItem<T = string> {
  value: T;
  label: string;
  translationKey?: string;
}

export const availableItemsFormatter: FieldFormatter<AvailableItemsFieldBase & { type: FieldType }> = {
  areSameType: (firstField, secondField) =>
    isHandledBySamePredicate(isAvailableItemsField)(firstField, secondField) && firstField.type === secondField.type,
  merge: mergeAvailableItemsFields,
  isHandled: isAvailableItemsField,
  withDefault: f => ({
    ...f,
    availableItems: [{ label: '', value: '', translationKey: undefined }, ...f.availableItems],
  }),
};

export const availableItemsValidator: FieldValueValidator<AvailableItemsFieldBase, string> = {
  isHandled: isAvailableItemsField,
  validateValue: (value, field) => field.availableItems.some(c => c.value === value),
};
